'use client';

// Legacy: pro-frontend/src/app/manage-portfolio/newpreview-portfolio/templates/
//   modern-template/modern-template.component.{ts,html,scss}

import { useMemo, useState } from 'react';

import './modern.css';

import type { PortfolioCategoryEntry } from '../../types';
import { getEntryField, getEntryLinks, isImageFileType } from './entry-helpers';
import { nl2brSanitize } from './sanitize-html';
import { SocialLinksNav } from './social-links';
import type { PortfolioTemplateProps } from './types';
import { useSectionScrollSpy } from './use-section-scrollspy';
import { getYoutubeEmbedUrl } from './youtube-embed';

export function ModernTemplate({ data, isPublicView, theme, primaryColor, editHref }: PortfolioTemplateProps) {
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<string | null>(null);

  const openImagePreview = (url: string) => setSelectedPreviewImage(url);
  const closeImagePreview = () => setSelectedPreviewImage(null);

  const summary = data.summary || data.shortBio;
  const summaryTitle = data.summaryTitle || 'Profile';
  const showSummary = data.summaryStatus === 1 || !data.summaryStatus;
  const showHighlights =
    (data.highlightsStatus === 1 || !data.highlightsStatus) && data.hightlights && data.hightlights.length > 0;
  const showDescriptions = data.descriptionStatus === 1 || !data.descriptionStatus;
  const showAttachments = data.attachmentStatus === 1 || !data.attachmentStatus;

  const hasEntries = data.categories.some((cat) => cat.entries && cat.entries.length > 0);
  const emptyState = !summary && !(data.hightlights && data.hightlights.length > 0) && !hasEntries;

  const sectionIds = useMemo(() => {
    const ids: string[] = [];
    if (showSummary && summary) ids.push('summary');
    if (showHighlights) ids.push('highlights');
    for (const cat of data.categories) {
      if (cat.entries && cat.entries.length > 0 && cat.id != null) {
        ids.push(String(cat.id));
      }
    }
    return ids;
  }, [data.categories, showHighlights, showSummary, summary]);

  const activeSectionId = useSectionScrollSpy(sectionIds);
  const designation = data.designation;

  function renderAttachments(entry: PortfolioCategoryEntry) {
    const files = entry.ResumeEntryFile || [];
    if (!showAttachments || files.length === 0) {
      return null;
    }
    const firstImage = files.find((f, idx) => isImageFileType(f.file_type) && idx === 0);
    const restImages = files.filter((f, idx) => idx !== 0 && isImageFileType(f.file_type));
    const nonImages = files.filter((f) => !isImageFileType(f.file_type));
    return (
      <div className="expe_attachmentDetails">
        {firstImage ? (
          <div className="row preview-row">
            <img
              src={firstImage.file_url}
              alt=""
              className="main-preview-img"
              onClick={() => openImagePreview(firstImage.file_url || '')}
            />
          </div>
        ) : null}

        {restImages.length > 0 ? (
          <div className="portfolio_attach_wrap clearfix">
            {restImages.map((file, idx) => (
              <div className="portfolio_incear remove_pading_porfolio clearfix" key={String(file.id ?? idx)}>
                <div className="files_in img_thum_in" onClick={() => openImagePreview(file.file_url || '')}>
                  <img src={file.file_url} alt="" />
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {nonImages.length > 0 ? (
          <div className="portfolio_attach_wrap clearfix">
            {nonImages.map((file, idx) => (
              <div className="portfolio_incear remove_pading_porfolio clearfix" key={String(file.id ?? idx)}>
                <a href={file.file_url} target="_blank" rel="noopener noreferrer" download>
                  <div className="files_in">
                    {file.file_type === 'application/pdf' || file.file_type === 'pdf' ? (
                      <i className="fa-solid fa-file-pdf" aria-hidden="true" />
                    ) : null}
                    {file.file_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                    file.file_type === 'application/msword' ? (
                      <i className="fa-solid fa-file-word" aria-hidden="true" />
                    ) : null}
                    {file.file_type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? (
                      <i className="fa-solid fa-file-excel" aria-hidden="true" />
                    ) : null}
                  </div>
                  <div className="files_in_wraps">
                    <p className="ellipsis">{file.file_name || file.file_url}</p>
                  </div>
                </a>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  function renderLinks(entry: PortfolioCategoryEntry) {
    const links = getEntryLinks(entry);
    if (links.length === 0) {
      return null;
    }
    return (
      <div>
        {links.map((link) => {
          const embedUrl = getYoutubeEmbedUrl(link.link);
          return (
            <div key={String(link.id ?? link.link)}>
              {embedUrl ? (
                <div className="video-embed">
                  <iframe src={embedUrl} allowFullScreen title="YouTube video" />
                </div>
              ) : link.link_meta_title ? (
                <div className="portfolio_in_site_link clearfix">
                  <div className="col-xs-3 inner_portfolio_public">
                    <img src={link.link_meta_image_url} alt="" />
                  </div>
                  <div className="col-xs-9 profolio_links_in_wrap">
                    <h3 className="ellipsis">{link.link_meta_title}</h3>
                    <h4 className="ellipsis">
                      <a href={link.link} target="_blank" rel="noopener noreferrer">
                        {link.link}
                      </a>
                    </h4>
                    <p className="ellipsis">{link.link_meta_description}</p>
                    <div className="profolio_links_in">
                      <a href={link.link} target="_blank" rel="noopener noreferrer">
                        Visit Link
                      </a>
                    </div>
                  </div>
                </div>
              ) : link.link ? (
                <div className="portfolio_in_site_link clearfix">
                  <div className="col-xs-12 profolio_links_in_wrap">
                    <h4 className="ellipsis">
                      <a href={link.link} target="_blank" rel="noopener noreferrer">
                        {link.link}
                      </a>
                    </h4>
                    <div className="profolio_links_in">
                      <a href={link.link} target="_blank" rel="noopener noreferrer">
                        Visit Link
                      </a>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }

  function entryTitle(entry: { display_title?: string; title?: string }): string {
    return String(entry.display_title || entry.title || 'Untitled');
  }

  return (
    <div
      className={`kw-tpl-root kw-tpl-modern porfolio_pulbic_view ${theme}${
        isPublicView ? ' is-public-view' : ''
      }`}
      style={{ '--primary-color': primaryColor } as React.CSSProperties}
    >
      <aside className="kw-tpl-sidebar porfolio_in_new_section">
        <div className="public_view_menu_port">
          {data.profile_image ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img alt="" src={data.profile_image} className="kw-portfolio-avatar" />
          ) : (
            <div className="kw-portfolio-avatar kw-portfolio-avatar-fallback" aria-hidden>
              {(data.name || '?').charAt(0)}
            </div>
          )}
          <div className="public_view_menu_profile_detail hidden-xs">
            <h3>{data.name}</h3>
            {designation ? <h4>{designation}</h4> : null}
            {data.company ? <h5>{data.company}</h5> : null}
            {!isPublicView && editHref ? (
              <a href={editHref} className="modern-edit-link" title="Edit Portfolio">
                <i className="fa-solid fa-pen" aria-hidden="true" /> Edit
              </a>
            ) : null}
          </div>
          <div className="public_view_menu_profile_in">
            <h3>{data.name}</h3>
            {(designation || data.company) ? (
              <h4>
                {designation}
                {data.company ? ` - ${data.company}` : ''}
              </h4>
            ) : null}
          </div>
        </div>

        <nav className="portfolio_menus_public_view portfolio_menus_public_view_scroll_wrap hidden-xs">
          <ul className="portfolio_menus_public_view_scroll_ul">
            {showSummary ? (
              <li>
                <a
                  href="#summary"
                  className={activeSectionId === 'summary' ? 'active' : undefined}
                >
                  {summaryTitle && summary ? summaryTitle : 'Profile'}
                </a>
              </li>
            ) : null}
            {showHighlights ? (
              <li>
                <a
                  href="#highlights"
                  className={activeSectionId === 'highlights' ? 'active' : undefined}
                >
                  Highlights
                </a>
              </li>
            ) : null}
            {data.categories.map((cat) =>
              cat.entries && cat.entries.length > 0 ? (
                <li key={String(cat.id ?? cat.title)}>
                  <a
                    href={`#${cat.id}`}
                    className={activeSectionId === String(cat.id) ? 'active' : undefined}
                  >
                    {cat.title}
                  </a>
                </li>
              ) : null,
            )}
          </ul>
        </nav>

        <div className="p5_portfolio_public_menu hidden-xs">
          <SocialLinksNav data={data} />
        </div>
      </aside>

      <main className="kw-tpl-main porfolio_public_view_detail">
        <div className="kw-tpl-main-inner porfolio_in_detail_task_inner">
          {showSummary ? (
            <section id="summary" className="kw-portfolio-section">
              <h2 className="for_summary_title">{summaryTitle && summary ? summaryTitle : 'Profile'}</h2>
              <div className="portfolio_in_detail_content kw-portfolio-richtext" id="section2">
                {summary ? <p dangerouslySetInnerHTML={{ __html: nl2brSanitize(summary) }} /> : null}
              </div>
            </section>
          ) : null}

          {showHighlights ? (
            <section className="portfolio_in_detail_content_art kw-portfolio-section" id="highlights">
              <h2>Highlights</h2>
              {data.hightlights.map((item) => (
                <div className="experience_title" key={String(item.id ?? item.display_title)}>
                  <h3 className="entry-title">{entryTitle(item)}</h3>
                  {renderAttachments(item)}
                  {renderLinks(item)}
                  <div className="detailed_section_portfolio gap_between_attachments_details">
                    {showDescriptions && (item.display_description || item.description) ? (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: nl2brSanitize(item.display_description || item.description),
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              ))}
            </section>
          ) : null}

          {data.categories.map((cat) =>
            cat.entries && cat.entries.length > 0 ? (
              <section
                className="portfolio_in_detail_content_art for_edit_icon_align kw-portfolio-section"
                id={String(cat.id)}
                key={String(cat.id ?? cat.title)}
              >
                <h2>{cat.title}</h2>
                {cat.entries.map((entry) => {
                  const workLocation = getEntryField(entry, 'work_location');
                  const title = entryTitle(entry);
                  const body = entry.display_description || entry.description || '';
                  return (
                    <div
                      className={`experience_title${entry.is_hidden ? ' hidden_activity' : ''}`}
                      key={String(entry.id ?? title)}
                    >
                      <div className="p5_experience_title_in check_all_advance premium_wdth_aligns clearfix">
                        <div className="entry-content-wrap">
                          <h3 className="entry-title">{title}</h3>
                          {entry.work_company || workLocation || getEntryField(entry, 'display_date') ? (
                            <div className="entry-meta">
                              {entry.work_company ? (
                                <span className="company-name">{entry.work_company}</span>
                              ) : null}
                              {entry.work_company && (workLocation || getEntryField(entry, 'display_date')) ? (
                                <span className="divider"> · </span>
                              ) : null}
                              {workLocation ? (
                                <span className="location">
                                  <i className="fa-solid fa-location-dot" aria-hidden="true" /> {workLocation}
                                </span>
                              ) : null}
                              {workLocation && getEntryField(entry, 'display_date') ? (
                                <span className="divider"> · </span>
                              ) : null}
                              {getEntryField(entry, 'display_date') ? (
                                <span className="date-pill">{getEntryField(entry, 'display_date')}</span>
                              ) : null}
                            </div>
                          ) : null}
                        </div>

                        {!isPublicView ? (
                          <div className="action-buttons title-action-buttons">
                            <span className="show-hide check_all_advance" title="Visible to public">
                              <i
                                className={entry.is_hidden ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'}
                                aria-label={entry.is_hidden ? 'Hidden' : 'Visible'}
                              />
                            </span>
                          </div>
                        ) : null}
                      </div>

                      {renderAttachments(entry)}
                      {renderLinks(entry)}

                      <div className="detailed_section_portfolio gap_between_attachments_details">
                        {showDescriptions && body ? (
                          <p
                            className="expe_descriptionDetails"
                            dangerouslySetInnerHTML={{ __html: nl2brSanitize(body) }}
                          />
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </section>
            ) : null,
          )}

          {emptyState ? (
            <div className="empty-state-wrap">
              <h3>Your portfolio is looking a bit empty...</h3>
              {!isPublicView && editHref ? (
                <h4>
                  You can always add experiences to your portfolio from the{' '}
                  <a href={editHref}>editor</a>.
                </h4>
              ) : null}
            </div>
          ) : null}
        </div>
      </main>

      {selectedPreviewImage ? (
        <div className="preview-overlay" onClick={closeImagePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={selectedPreviewImage} className="preview-img" alt="Enlarged view" />
            <div className="preview-close" onClick={closeImagePreview}>
              <i className="fa-solid fa-xmark" aria-hidden="true" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
